import colors from '@sportninja/common/constants/appColors'
import { BillingItem } from '@sportninja/common/hooks/useRegistrationDetails'
import { formatCurrency } from '@sportninja/common/utils/utils'
import { BillingTotals } from './types'

/**
 * Status ID to display information mapping
 * Based on registration status options
 */
export const STATUS_MAP: Record<number, { label: string; color: string }> = {
  1: { label: 'Submitted', color: colors.INFO_200 },
  2: { label: 'Pending', color: colors.WARNING_200 },
  3: { label: 'Approved', color: colors.SUCCESS_300 },
  4: { label: 'Approved w/ Conditions', color: colors.WARNING_200 },
  5: { label: 'Cancelled', color: colors.ERROR_200 },
  6: { label: 'In Progress', color: colors.INFO_200 },
}

/**
 * Calculate billing totals from billing array
 * Logic adapted from RegistrationBillingForm
 */
export const calculateBillingTotals = (
  billing: BillingItem[] | undefined,
  isPlayerRegistration: boolean
): BillingTotals => {
  if (!billing || billing.length === 0) {
    return {
      teamCost: 0,
      outstanding: 0,
      paid: 0,
      totalOutstanding: 0,
      totalPaid: 0,
    }
  }

  // Filter billing items based on user type (player or teamRep)
  const filteredBilling = billing.filter((el) =>
    isPlayerRegistration
      ? el.user_type === 'player'
      : el.user_type === 'teamRep'
  )

  // Team Cost: Sum of all subtotals
  const teamCost = filteredBilling.reduce((acc, el) => acc + el.subtotal, 0)

  // Total Paid: Sum of paid items display_subtotal
  const totalPaid = filteredBilling
    .filter((el) => el.paid)
    .reduce((acc, el) => acc + el.display_subtotal, 0)

  // Total Outstanding: Unpaid + partial refunds
  let totalOutstanding = 0
  filteredBilling
    .filter((el) => !el.paid || el.refunded)
    .forEach((el) => {
      // Handle partial refunds by calculating the difference
      // between original subtotal and refunded amount
      if (el.refunded && el.refund_subtotal < el.subtotal) {
        totalOutstanding += el.subtotal - el.refund_subtotal
      } else if (!el.paid) {
        // For regular unpaid items, add the full subtotal
        totalOutstanding += el.subtotal
      }
    })

  return {
    teamCost,
    outstanding: totalOutstanding,
    paid: totalPaid,
    totalOutstanding,
    totalPaid,
  }
}

/**
 * Get the next due date from billing items (first unpaid item)
 */
export const getNextDueDate = (
  billing: BillingItem[] | undefined,
  isPlayerRegistration: boolean
): string | null => {
  if (!billing) {
    return null
  }

  const filteredBilling = billing.filter((el) =>
    isPlayerRegistration
      ? el.user_type === 'player'
      : el.user_type === 'teamRep'
  )

  const nextUnpaid = filteredBilling.find((el) => !el.paid)
  return nextUnpaid?.due_date || null
}

/**
 * Format currency value or return dash for null/zero
 */
export const formatBillingValue = (
  value: number | null | undefined,
  currency: string | null | undefined
): string => {
  if (value === null || value === undefined) {
    return '-'
  }
  if (currency) {
    return formatCurrency(value, currency)
  }
  return `$${value.toFixed(2)}`
}

/**
 * Get status info from status ID
 */
export const getStatusInfo = (
  statusId: number | undefined
): { label: string; color: string } => {
  return STATUS_MAP[statusId || 3] || STATUS_MAP[3]
}
